import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getAlarms } from "redux/actions/setAlarmActions";
import { getMeetings } from "redux/actions/setMeetingActions";
import { getTestsCountByPsych } from "redux/actions/setTestActions";
import { getSchools } from "redux/actions/setSchoolActions";
import { classNames } from "helpers/classNames";
import { checkIosPwa } from "helpers/devices";
import { downloadFileInPWA } from "helpers/pwa";
import { Loader } from "components/Loader";
import { useFetchPupilsWithCategories } from "hooks/useQuery";
import { YanaoComponent } from "helpers/YanaoComponent";
import { IS_PRODUCTION, SITE_URL } from "config";
import infoIcon from "static/img/ic-info-black.svg";
import informationForParents from "static/docs/information_for_parents.pdf";
import presentationForParents from "static/docs/presentation_for_parents.pdf";
import psychInstruction from "static/docs/psych_instruction.pdf";
import { NewsSupportButton } from "./NewsSupportButton";

const eljurConnectUrl = `https://school.lpr-reg.ru/auth/test-U-external?client_id=test-U-external&response_type=code&redirect_uri=${SITE_URL}/eljur`;

const documents = {
    informationForParents: {
        file: informationForParents,
        fileName: "information_for_parents.pdf",
    },
    presentationForParents: {
        file: presentationForParents,
        fileName: "presentation_for_parents.pdf",
    },
    psychInstruction: {
        file: psychInstruction,
        fileName: "Инструкция_по_использованию_платформы_TestU.pdf",
    },
};

export const PsychProfile = ({ loading }) => {
    const dispatch = useDispatch();
    const { alarms } = useSelector((state) => state.alarm);
    const { schools } = useSelector((state) => state.school);
    const { meetings } = useSelector((state) => state.meeting);
    const { testsCount } = useSelector((state) => state.test);
    const { user } = useSelector((state) => state.auth);
    const { pupilsWithAllCategories } = useFetchPupilsWithCategories();
    const [riskPupilsCount, setRiskPupilsCount] = useState(0);
    const [alarmsLength, setAlarmsLength] = useState(0);
    const { t } = useTranslation("profile");

    useEffect(() => {
        dispatch(getMeetings());
        dispatch(getTestsCountByPsych());
        dispatch(getSchools());
        dispatch(getAlarms());
    }, [dispatch]);

    useEffect(() => {
        if (pupilsWithAllCategories?.length) {
            setRiskPupilsCount(pupilsWithAllCategories.length);
        }
    }, [pupilsWithAllCategories]);

    useEffect(() => {
        if (alarms.length) {
            // Запрос считается непрочитанным если:
            // 1. Нет сообщения: только !readAt
            // 2. Есть сообщение: !readAt ИЛИ !messageReadAt (оба должны быть прочитаны)
            const getAlarmsLength = alarms.filter(({ readAt, message, messageReadAt }) => {
                if (!message) {
                    // Запрос без сообщения - проверяем только readAt
                    return !readAt;
                } else {
                    // Запрос с сообщением - оба должны быть прочитаны
                    return !readAt || !messageReadAt;
                }
            });

            setAlarmsLength(getAlarmsLength.length);
        } else {
            setAlarmsLength(0);
        }
    }, [alarms]);

    const onClickHandler = async (file) => {
        const fileInfo = documents[file];

        if (!fileInfo) return;

        if (checkIosPwa()) {
            await downloadFileInPWA(fileInfo.file, fileInfo.fileName);
        } else {
            window.open(fileInfo.file, "_blank");
        }
    };

    return (
        <div className="profile-page__entities">
            <Link
                to="/alarms"
                className={classNames("profile-page__psych-alarm", {
                    "profile-page__psych-alarm_notice": alarmsLength,
                })}
            >
                <div>
                    <p>{t("help_requests")}</p>
                    <p>{t("show_who_needs_help")}</p>
                </div>
                <span>{alarmsLength}</span>
            </Link>

            <YanaoComponent componentName={"NewsSupportButtonYanao"}>
                <NewsSupportButton />
            </YanaoComponent>

            {user?.isEljurCanLinked && (
                <a href={`${eljurConnectUrl}/${user._id}/psych`} className="profile-page__psych-ejlur-connect">
                    <p>Привязать аккаунт ЭлЖур</p>
                    <span>
                        <img src={infoIcon} alt="" />
                    </span>
                </a>
            )}

            {loading ? (
                <Loader />
            ) : (
                <div className="profile-page__entities-wrapper" style={{ paddingBottom: 24 }}>
                    <Link to="/schools" className={`entity ${!!schools.length && "entity_school"}`}>
                        <p className={`entity__title ${!!schools.length && "entity__title_white"}`}>{t("schools")}</p>
                        {!schools.length && <p className="entity__description">{t("add_school")}</p>}
                        <span className={`entity__extra ${!schools.length && "entity__extra_psych-null"}`}>
                            {!!schools.length && schools.length}
                        </span>
                    </Link>
                    <Link to="/meetings" className={`entity ${meetings.length ? "entity_meeting" : null}`}>
                        <p className={`entity__title ${meetings.length ? "entity__title_white" : null}`}>
                            {t("meetings")}
                        </p>
                        {!meetings.length && <p className="entity__description">{t("add_meeting")}</p>}
                        <span className={`entity__extra ${!meetings.length ? "entity__extra_psych-null" : null}`}>
                            {meetings.length ? meetings.length : null}
                        </span>
                    </Link>
                    <Link to="/statistic/schools" className={`entity ${!!testsCount && "entity_statistic"}`}>
                        <p className={`entity__title ${testsCount ? "entity__title_white" : null}`}>
                            {t("psychodiagnostics_results")}
                        </p>
                        {!testsCount && <p className="entity__description">{t("no_data_for_analysis")}</p>}
                        {!!testsCount && <span className="entity__extra entity__extra_statistic"></span>}
                    </Link>
                    <Link to="/workWithMethods" className="entity entity_workWithMethods">
                        <p className="entity__title entity__title_white">{t("working_with_methods")}</p>

                        <span className="entity__extra entity__extra_workWithMethods"></span>
                    </Link>

                    {/* Рекомендации */}
                    {!IS_PRODUCTION && (
                        <Link to="/advices/schools" className="entity entity_recommendations">
                            <p className="entity__title entity__title_white">{t("recommendations")}</p>
                            <span className="entity__extra entity__extra_workWithMethods"></span>
                        </Link>
                    )}

                    <Link to="/riskGroups" className={`entity ${!!riskPupilsCount && "entity_riskGroups"}`}>
                        <p className={`entity__title ${riskPupilsCount ? "entity__title_white" : null}`}>
                            {t("risk_groups")}
                        </p>
                        <span className={`entity__extra ${!riskPupilsCount && "entity__extra_psych-null"}`}>
                            {!!riskPupilsCount && riskPupilsCount}
                        </span>
                    </Link>
                    <div className="entity entity_instruction" onClick={() => onClickHandler("psychInstruction")}>
                        <p className="entity__title entity__title_white">{t("instruction")}</p>
                    </div>
                    <Link to="/management-reporting" className="entity entity_meeting">
                        <p className="entity__title entity__title_white">{t("management_reporting")}</p>
                    </Link>
                    <Link to="/guidelines" className="entity entity_workWithMethods">
                        <p className="entity__title entity__title_white">{t("guidelines")}</p>
                    </Link>
                    <Link to="/sociometry/schools" className={`entity ${!!testsCount && "entity_statistic"}`}>
                        <p className={`entity__title entity__title_white`}>Результаты Социометрии</p>
                        <span className="entity__extra entity__extra_statistic"></span>
                    </Link>
                </div>
            )}

            <button
                onClick={() => onClickHandler("informationForParents")}
                className="profile-page__psych-parents-info"
            >
                <div>
                    <p>{t("information_btn_text")}</p>
                </div>
                <span>
                    <img src={infoIcon} alt="" />
                </span>
            </button>
            <button
                onClick={() => onClickHandler("presentationForParents")}
                className="profile-page__psych-parents-info"
            >
                <div>
                    <p>{t("presentation_btn_text")}</p>
                </div>
                <span>
                    <img src={infoIcon} alt="" />
                </span>
            </button>
        </div>
    );
};
